import { memo, useMemo, useState } from 'react';
import { useActivePartners } from '@/hooks/usePartners';
import { Skeleton } from '@/components/ui/skeleton';
import { HighlightedHeading } from '@/components/ui/highlighted-heading';
import { proxyImage } from '@/lib/imageProxy';

interface Partner {
  id: string;
  name: string;
  logo_url: string;
  redirect_url: string | null;
  logo_size: number | null;
}

// Composant mémorisé pour éviter les re-renders
const PartnerLogo = memo(({ partner }: { partner: Partner }) => {
  const logoHeight = partner.logo_size || 100;
  // Si le proxy échoue, on retombe sur l'URL d'origine (logo plus lourd mais
  // jamais cassé). Le flag évite toute boucle d'onError.
  const [proxyFailed, setProxyFailed] = useState(false);
  // Explicit dimensions to prevent layout shifts (CLS)
  const imgProps = {
    // Servi redimensionné + WebP via weserv (le transform Lovable/Supabase est
    // mort en prod : les logos bruts faisaient jusqu'à 786 Ko en 1200×1200).
    // ~220px large suffit largement (affichés < 160px).
    src: proxyFailed ? partner.logo_url : proxyImage(partner.logo_url, { width: 220 }),
    alt: `Logo ${partner.name} - Partenaire Maison Eridia`,
    className: "partner-logo w-auto opacity-90",
    // lazy : la section est sous le hero ; ne pas voler de bande passante au LCP.
    loading: "lazy" as const,
    width: 160,
    height: logoHeight,
    style: { '--logo-height': `${logoHeight}px` } as React.CSSProperties,
    onError: () => { if (!proxyFailed) setProxyFailed(true); },
  };
  
  if (partner.redirect_url) {
    return (
      <a href={partner.redirect_url} target="_blank" rel="noopener noreferrer" className="block">
        <img {...imgProps} />
      </a>
    );
  }
  
  return <img {...imgProps} />;
});

PartnerLogo.displayName = 'PartnerLogo';

const TrustedClientsSection = () => {
  const { data: partners, isLoading } = useActivePartners();

  // Fallback pour les données hardcodées si la base est vide
  const fallbackClients: Partner[] = useMemo(() => [
    {
      id: 'fallback-1',
      name: "Crousty'Z",
      logo_url: '/lovable-uploads/0618d7c8-05df-4fdf-bfbb-1734543436e5.png?width=160&quality=90&format=webp',
      redirect_url: 'https://croustyz.fr/',
      logo_size: 100,
    },
    {
      id: 'fallback-2',
      name: 'Casino',
      logo_url: '/lovable-uploads/f0c08a53-eb9e-4f3f-b1eb-66aff6948b25.png?width=160&quality=90&format=webp',
      redirect_url: null,
      logo_size: 100,
    },
    {
      id: 'fallback-3',
      name: 'CasaSpa',
      logo_url: '/lovable-uploads/5d7a027c-76f6-46ea-9772-65b907ca03d2.png?width=160&quality=90&format=webp',
      redirect_url: null,
      logo_size: 100,
    },
    {
      id: 'fallback-4',
      name: 'Didaticia',
      logo_url: '/lovable-uploads/e7a9fb40-c73c-462d-9df5-c5f322d7ad1f.png?width=160&quality=90&format=webp',
      redirect_url: null,
      logo_size: 100,
    },
    {
      id: 'fallback-5',
      name: 'Qenetra',
      logo_url: '/lovable-uploads/cb3f6f56-8a5a-4526-9db9-9ffd2735fcb7.png?width=160&quality=90&format=webp',
      redirect_url: null,
      logo_size: 100,
    },
    {
      id: 'fallback-6',
      name: 'Aluma Consulting',
      logo_url: '/lovable-uploads/088a8ce6-d35a-4319-b6dc-286b88f8ed09.png?width=160&quality=90&format=webp',
      redirect_url: null,
      logo_size: 100,
    },
    {
      id: 'fallback-7',
      name: 'Art & Food',
      logo_url: '/lovable-uploads/art-food-logo.png?width=160&quality=90&format=webp',
      redirect_url: 'https://artx-food.com/',
      logo_size: 100,
    },
  ], []);

  const displayedPartners = useMemo(() => 
    partners && partners.length > 0 ? partners : fallbackClients,
    [partners, fallbackClients]
  );

  if (isLoading) {
    return (
      <section aria-label="Références entreprises – évènements" className="md:py-10 bg-background py-[16px]">
        <div className="container mx-auto px-4 text-center">
          <Skeleton className="h-8 w-64 mx-auto mb-3" />
          <Skeleton className="h-5 w-96 mx-auto mb-8" />
          <div className="flex gap-8 justify-center">
            {[1, 2, 3, 4, 5].map((i) => (
              <Skeleton key={i} className="w-32 h-20" />
            ))}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section aria-label="Références entreprises – évènements" className="md:py-10 bg-background py-[16px]">
      {/* Styles CSS purs avec media queries - pas de JS pour éviter re-renders */}
      <style dangerouslySetInnerHTML={{
        __html: `
          .partners-carousel {
            overflow: hidden;
            width: 100%;
            -webkit-mask-image: linear-gradient(to right, transparent, black 5%, black 95%, transparent);
            mask-image: linear-gradient(to right, transparent, black 5%, black 95%, transparent);
          }
          
          .partners-track {
            display: flex;
            width: max-content;
            /* IMPORTANT: index.html has a global mobile rule forcing animation-duration: 0.2s !important.
               We must override it here for this specific carousel. */
            animation: partnersScroll 40s linear infinite !important;
            backface-visibility: hidden;
            -webkit-backface-visibility: hidden;
            perspective: 1000px;
            transform: translateZ(0);
          }
          
          .partner-item {
            flex-shrink: 0;
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 8px;
            margin: 0 32px;
            width: 160px;
            height: 112px;
          }
          
          .partner-logo {
            max-height: var(--logo-height, 100px);
            object-fit: contain;
          }
          
          @media (max-width: 768px) {
            .partners-track {
              animation-duration: 50s !important;
            }
            
            .partner-item {
              margin: 0 20px;
              width: 112px;
              height: 80px;
            }
            
            .partner-logo {
              max-height: calc(var(--logo-height, 100px) * 0.75);
            }
          }
          
          @media (hover: hover) {
            .partners-track:hover {
              animation-play-state: paused;
            }
          }
          
          @keyframes partnersScroll {
            0% { transform: translateX(0) translateZ(0); }
            100% { transform: translateX(-50%) translateZ(0); }
          }
        `
      }} />
      
      <div className="container mx-auto px-4 text-center">
        <div>
          <HighlightedHeading
            highlightedText="confiance"
            className="text-3xl md:text-4xl mb-4"
          >
            Ils nous font confiance
          </HighlightedHeading>
          <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
            Des entreprises qui nous confient l'organisation de leurs évènements en Île-de-France.
          </p>
        </div>

        <div className="partners-carousel">
          <div className="partners-track">
            {/* Premier groupe de logos */}
            {displayedPartners.map((partner) => (
              <div key={`first-${partner.id}`} className="partner-item">
                <PartnerLogo partner={partner} />
              </div>
            ))}
            
            {/* Duplication pour boucle infinie */}
            {displayedPartners.map((partner) => (
              <div key={`second-${partner.id}`} className="partner-item">
                <PartnerLogo partner={partner} />
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default TrustedClientsSection;
